import { getRuntimeConfig } from "../types/RuntimeConfig";
import { callUnauthorizedHandler } from "./fleetApi";

// #C — was VITE_ADMIN_API_BASE / VITE_ADMIN_FLEET_API_BASE. Now read from
// the runtime-config envelope hydrated by <ConfigProvider> (sub-card #B) into
// window.__2HIVES_CONFIG__. CI deploy no longer needs to template VITE_*
// into the bundle — the /v1/runtime-config Lambda (sub-card #A) publishes
// the current API GW id at request time so an API GW id rotation never
// strands clients.
// #2546 — removed raw API GW URL fallbacks and eager ADMIN_API_BASE /
// ADMIN_FLEET_API_BASE constants; all call sites now invoke the getter
// directly so the bundle never embeds a raw API GW URL.
const FALLBACK_ADMIN_API_BASE = "https://api.2hives.ai";

export function getAdminApiBase(): string {
  return getRuntimeConfig()?.apiBaseUrl ?? FALLBACK_ADMIN_API_BASE;
}

// AdminFleet stack (#2216) — sibling REST API that hosts /fleet/* /db/* /ocnode/*
// /platform/* AND, since 2026-06-01, the new customer feature routes added when
// TwoHivesAdmin hit the CFN 500-resource cap (#2226):
//   - POST   /admin/hives/{name}/export       (#2232)
//   - POST   /admin/cards/{id}/share          (#2233)
//   - GET    /admin/cards/{id}/share          (#2233)
//   - DELETE /admin/cards/{id}/share/{token}  (#2233)
//   - GET    /share/{token}                   (#2233 PUBLIC, no auth)
//   - POST   /v1/transcribe                   (#2234)
//
// The runtime-config envelope's `apiBaseUrl` will eventually consolidate both
// legacy bases behind one canonical fleet/admin URL (sub-card #A). Until that
// landing, AdminFleet base uses the same canonical fallback.
export function getAdminFleetApiBase(): string {
  return getRuntimeConfig()?.apiBaseUrl ?? FALLBACK_ADMIN_API_BASE;
}

export class ApiError extends Error {
  // #2944 — `body` carries the parsed JSON envelope (when the server returned
  // JSON) so callers can render structured error UX (e.g. `tier_locked` with
  // a human `detail` + `upgrade_url`) without re-fetching or string-matching
  // `message`. Older call sites that only read `message`/`status` continue to
  // work unchanged. May be {} when the body wasn't parseable JSON.
  //
  // #3034 — `code` is the canonical machine-readable token from the backend's
  // unified error envelope (`{code, message, error, upgrade_url?, details?}`).
  // It mirrors `body.code` (or `body.error` for legacy single-key bodies) and
  // is the safe key for `switch` / equality checks in callers — never use
  // `message` (a human sentence that the backend may rephrase) for branching.
  // Existing #2944 code that compares `body.error === "tier_locked"` keeps
  // working unchanged because the backend continues to emit `error` as an
  // alias of `code`.
  public readonly code: string;

  constructor(
    public readonly status: number,
    message: string,
    public readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
    Object.setPrototypeOf(this, ApiError.prototype);
    this.code = extractErrorCode(body) ?? "";
  }
}

/**
 * Pull the machine-readable error code out of a parsed response body.
 *
 * Precedence (#3034 canonical envelope, with back-compat fall-throughs):
 *   1. `body.code`       — canonical key emitted by `_resp_error` /
 *                          `normalize_error_body` post-#3034.
 *   2. `body.error`      — legacy single-key bodies; tier_locked envelope.
 *                          Still emitted as an alias of `code` by the
 *                          backend so this branch is mostly future-proofing
 *                          for older deployed boxes.
 *
 * Returns null when neither key is present or when the value isn't a string
 * — callers should treat that as "no machine code; render `message`".
 */
function extractErrorCode(body: Record<string, unknown>): string | null {
  const code = body.code;
  if (typeof code === "string" && code.length > 0) return code;
  const err = body.error;
  if (typeof err === "string" && err.length > 0) return err;
  return null;
}

/**
 * Pull the human-readable message out of a parsed response body.
 *
 * Precedence (#3034 canonical envelope, with back-compat fall-throughs):
 *   1. `body.message`    — canonical sentence in the post-#3034 envelope.
 *   2. `body.detail`     — #2944 tier_locked envelope's human field.
 *   3. `body.error`      — legacy single-key bodies where the only string
 *                          was the `error` value itself.
 *   4. synthesised       — `${method} ${path} → ${status}`.
 */
function extractErrorMessage(
  body: Record<string, unknown>,
  method: string,
  path: string,
  status: number,
): string {
  const msg = body.message;
  if (typeof msg === "string" && msg.length > 0) return msg;
  const detail = body.detail;
  if (typeof detail === "string" && detail.length > 0) return detail;
  const err = body.error;
  if (typeof err === "string" && err.length > 0) return err;
  return `${method} ${path} → ${status}`;
}

function getToken(): string | null { return localStorage.getItem("jwt"); }

function authHdr(): HeadersInit {
  const t = getToken();
  return t
    ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

export async function adminFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  // #2542 — call getAdminApiBase() at fetch time so post-hydration config is used.
  const res = await fetch(`${getAdminApiBase()}${path}`, {
    method,
    headers: authHdr(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    if (res.status === 401 && path === "/admin/user/me") {
      callUnauthorizedHandler();
    }
    const b = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(
      res.status,
      // #3034 — unified envelope parser. Reads `message` (canonical), falls
      // back to `detail` (#2944 tier_locked), then `error` (legacy), then a
      // synthesised summary. See extractErrorMessage docstring.
      extractErrorMessage(b, method, path, res.status),
      b,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Same shape as `adminFetch` but routes to the AdminFleet REST API
 * (`ADMIN_FLEET_API_BASE`). Use for #2232/#2233/#2234 endpoints (and any
 * other routes wired on TwoHivesAdminFleet — search the stack for the route
 * tree). NEVER carries a body different from JSON; for binary uploads
 * (e.g. POST /v1/transcribe) call fetch() directly with the FormData.
 */
export async function adminFleetFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  // #2542 — call getAdminFleetApiBase() at fetch time so post-hydration config is used.
  const res = await fetch(`${getAdminFleetApiBase()}${path}`, {
    method,
    headers: authHdr(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(
      res.status,
      // #3034 — see comment in adminFetch above.
      extractErrorMessage(b, method, path, res.status),
      b,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── User profile ──────────────────────────────────────────────────────────────

export interface UserProfile {
  sub:               string;
  email:             string;
  linux_username:    string;
  lxc_box_id:        string;
  plan:              string;
  status:            string;
  fleet_role:        string | null;
  created_at:        string;
  integration_count?: number;
  avatar_url?:        string | null;
  display_name?:      string | null;
  // Billing fields
  tier?:               "basic" | "tier1" | "demo" | "paid_shared" | "paid_dedicated"; // "demo"/"paid_shared" kept for backward compat
  tier_expires_at?:    string | null;
  balance_cents?:      number;
  tokens_used_today?:  number;
  token_budget_daily?: number;
  stripe_customer_id?: string;
  // #2325 — persisted dark/light/system preference
  theme_preference?:   "dark" | "light" | "system" | null;
  // #2256 — onboarding wizard completion timestamp (ISO); null = not yet done
  onboarding_completed_at?: string | null;
}

export interface UsagePeriod {
  used:         number;
  cap?:         number | null;
  trailing_avg?: number;
  projected_usd?: number | null;
  start:        string;
  end:          string;
}

export interface HiveUsage {
  name:           string;
  spend_usd:      number;
  max_budget_usd: number;
  pct_used:       number;
}

export interface UsageStats {
  daily:   UsagePeriod & { cap: number | null };
  weekly:  UsagePeriod & { trailing_avg: number };
  monthly: UsagePeriod & { trailing_avg: number; projected_usd: number | null };
  daily_history: Array<{ date: string; tokens: number }>;
  tier?:     string;
  per_hive?: HiveUsage[];   // per-hive spend (#1806); LiteLLM team spend per hive
}

export interface SubscriptionDetails {
  plan:                   string;
  status:                 "active" | "cancelled" | "past_due" | "trialing" | string;
  current_period_end?:    string | null;
  next_billing_date?:     string | null;
  card_last4?:            string | null;
  card_brand?:            string | null;
  cancel_at_period_end?:  boolean;
}

export interface BillingInvoice {
  id:                  string;
  created:             string;
  amount_paid:         number;
  currency:            string;
  status:              "paid" | "open" | "draft" | string;
  hosted_invoice_url?: string;
  description?:        string;
}

export const billingApi = {
  getPortal: (): Promise<{ url: string }> =>
    adminFetch("GET", "/admin/billing/portal"),

  getCheckout: (successUrl: string, cancelUrl: string): Promise<{ url: string }> =>
    adminFetch("POST", "/admin/billing/checkout", { success_url: successUrl, cancel_url: cancelUrl }),

  getSubscription: (): Promise<SubscriptionDetails> =>
    adminFetch("GET", "/admin/billing/subscription"),

  listInvoices: (): Promise<BillingInvoice[]> =>
    adminFetch<{ invoices: BillingInvoice[] }>("GET", "/admin/billing/invoices")
      .then((r) => r.invoices ?? []),

  getUsage: (): Promise<UsageStats> =>
    adminFetch("GET", "/admin/usage"),
};

export const userApi = {
  getMe:    (): Promise<UserProfile> => adminFetch("GET",    "/admin/user/me"),
  // Legacy stub — kept so older callers don't break; the new 7-day flow lives
  // under accountApi below (#2254 GDPR Article 17).
  deleteMe: (): Promise<void>        => adminFetch("DELETE", "/admin/user/me"),
};

// ── Operator / platform-admin user management (#2256) ────────────────────────

/** Operator-only: fetch any user's full profile by cognito_sub. */
export const adminUserApi = {
  getUser: (sub: string): Promise<UserProfile> =>
    adminFetch("GET", `/admin/users/${encodeURIComponent(sub)}`),

  /**
   * Operator reset: clears onboarding_completed_at so the wizard shows again
   * on the user's next /hives visit.
   */
  resetOnboarding: (sub: string): Promise<void> =>
    adminFetch("PATCH", `/admin/users/${encodeURIComponent(sub)}`, {
      onboarding_completed_at: null,
    }),
};

// ── Account deletion + data export (GDPR Article 17 + 20, #2254) ─────────────

export interface AccountDeleteInitResponse {
  ok:             boolean;
  confirm_token:  string;
  expires_at:     string;
  window_days:    number;
}

export interface AccountDeleteStatus {
  pending_deletion:   boolean;
  delete_at:          string | null;
  seconds_remaining:  number | null;
  window_days:        number;
}

export interface AccountExportResponse {
  ok:           boolean;
  export_id:    string;
  download_url: string;
  expires_in:   number;
  size_bytes:   number;
}

export interface AccountExportStatus {
  last_export_at:  string | null;
  last_export_url: string | null;
}

export const accountApi = {
  deleteInit:    (): Promise<AccountDeleteInitResponse> =>
    adminFetch("POST", "/admin/account/delete"),
  deleteConfirm: (confirm_token: string): Promise<{ ok: boolean; delete_at: string; window_days: number }> =>
    adminFetch("POST", "/admin/account/delete/confirm", { confirm_token }),
  deleteCancel:  (): Promise<{ ok: boolean; pending_deletion: boolean }> =>
    adminFetch("POST", "/admin/account/delete/cancel"),
  deleteStatus:  (): Promise<AccountDeleteStatus> =>
    adminFetch("GET", "/admin/account/delete/status"),
  // Legacy (danger-zone page) — routed via TwoHivesAdmin /admin/account/export
  exportData:    (): Promise<AccountExportResponse> =>
    adminFetch("POST", "/admin/account/export"),
  exportStatus:  (): Promise<AccountExportStatus> =>
    adminFetch("GET", "/admin/account/export/status"),
  // #2458 customer self-serve export — routed via TwoHivesAdminFleet.
  // #2963: revert to `/admin/account/*`. The CF distribution only forwards
  // `/admin/account/*` + `/admin/internal/account/*` to the fleet API; the
  // bare `/account/*` path is CORS-blocked at the edge. The `/admin/*` API
  // prefix is intentional (#2087 follow-up: only browser routes dropped
  // `/admin`, backend API paths kept it).
  customerExport: (): Promise<AccountExportResponse> =>
    adminFleetFetch("POST", "/admin/account/data-export"),
  customerExportStatus: (): Promise<AccountExportStatus> =>
    adminFleetFetch("GET", "/admin/account/data-export/status"),
};

// ── SSH key management (#1416) ────────────────────────────────────────────────

export interface SshKeyPublic {
  fingerprint:       string;
  public_key_prefix: string; // first 16 chars
  public_key_suffix: string; // last 16 chars
  created_at:        string;
  download_consumed: boolean;
}

export interface SshKeyGenerateResult {
  private_key_pem: string;
  fingerprint:     string;
}

export interface SshKeyDownloadResult {
  url: string;
}

export const sshKeyApi = {
  /** GET /admin/profile/ssh-key/public — returns fingerprint + truncated public key */
  getPublic: (): Promise<SshKeyPublic | null> =>
    adminFetch<SshKeyPublic>("GET", "/admin/profile/ssh-key/public").catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }),

  /** POST /admin/profile/ssh-key/generate → 201 — returns private key PEM once */
  generate: (): Promise<SshKeyGenerateResult> =>
    adminFetch("POST", "/admin/profile/ssh-key/generate"),

  /** POST /admin/profile/ssh-key/download-url → 200 once, 410 if consumed */
  downloadUrl: (): Promise<SshKeyDownloadResult> =>
    adminFetch("POST", "/admin/profile/ssh-key/download-url"),

  /** POST /admin/profile/ssh-key/rotate → 200 — invalidates previous key, returns new private key PEM */
  rotate: (): Promise<SshKeyGenerateResult> =>
    adminFetch("POST", "/admin/profile/ssh-key/rotate"),
};

// ── Sync context for the Mutagen 2-way-sync section (#3204) ───────────────────
// Powers the per-hive `mutagen sync create` command rendered on /profile/ssh-key.
// Hidden until an SSH key exists (per #3185); the section component reads
// `getPublic()` first and only fetches this envelope when a key is available.

export interface SyncContextHive {
  /** Hive slug — lowercased alphanumeric + dash/underscore. */
  slug:          string;
  /** Absolute path on the user's LXC container (or "projects/<slug>" if the
   *  linux username hasn't been provisioned yet — render with a note). */
  project_path:  string;
}

export interface SyncContext {
  /** Linux uid name on the user's LXC container — used as the SSH login. */
  linux_username: string;
  /** Fully-qualified host the user's container is reachable on over SSH. */
  tenant_host:    string;
  /** Visible (cloning + active) hives the user can sync. Stable alpha-sort. */
  hives:          SyncContextHive[];
}

export const syncContextApi = {
  /** GET /admin/profile/sync-context — returns linux_username + tenant_host + hives[] */
  get: (): Promise<SyncContext> =>
    adminFetch("GET", "/admin/profile/sync-context"),
};

// ── OAuth tokens (per-user, multi-account) ────────────────────────────────────

/** #1453 — per-credential hive access control (spec
 *  `specs/integrations-access-control-v0.2.md`). `"all"` = every hive in the
 *  tenant may use this credential; `string[]` = only the listed hive names.
 *  Default for legacy/back-filled rows is `"all"`. */
export type HiveScope = "all" | string[];

/** #1453 — body shape for PATCH `.../grant` endpoints. */
export interface CredentialGrant {
  hive_scope:              HiveScope;
  include_invited_members: boolean;
}

export interface OAuthAccount {
  account_id:     string;
  account_email?: string;
  expires_at?:    number;
  scopes?:        string[];
  // #1453 — per-credential hive grant. Optional on the wire so the legacy
  // shape (pre-#1452 backend) still parses; UI treats absent as `"all"`/true.
  hive_scope?:              HiveScope;
  include_invited_members?: boolean;
}

export interface OAuthProvider {
  name:         string;
  label:        string;
  description?: string;
  accounts:     OAuthAccount[];
}

export const oauthTokenApi = {
  listProviders: (): Promise<OAuthProvider[]> =>
    adminFetch("GET", "/admin/oauth/providers"),

  start: (provider: string): Promise<{ authorize_url: string; state: string }> =>
    adminFetch("POST", `/admin/oauth/start?provider=${encodeURIComponent(provider)}`),

  disconnect: (provider: string, accountId: string): Promise<void> =>
    adminFetch("DELETE", `/admin/oauth/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}`),

  /** #1453 — PATCH `/admin/integrations/oauth/<provider>/<account_id>/grant`.
   *  Sets `hive_scope` + `include_invited_members` on a single OAuth account
   *  row. Owner / tenant-admin only (server-enforced). */
  setGrant: (provider: string, accountId: string, grant: CredentialGrant): Promise<void> =>
    adminFetch(
      "PATCH",
      `/admin/integrations/oauth/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/grant`,
      grant,
    ),
};

// ── Magic-link login (#2318) ──────────────────────────────────────────────────
// Unauthenticated endpoints. request() always resolves 200 (no account
// enumeration); redeem() returns a Cognito session on success.

export interface MagicLinkRedeemResult {
  id_token:       string;
  refresh_token?: string;
}

export const magicLinkApi = {
  request: (email: string): Promise<void> =>
    adminFetch("POST", "/admin/auth/magic-link", { email }),

  redeem: (token: string): Promise<MagicLinkRedeemResult> =>
    adminFetch("GET", `/admin/auth/redeem/${encodeURIComponent(token)}`),
};

// ── API key integrations (static-key providers) ───────────────────────────────

export interface ApiKeyEntry {
  key_label:  string;
  created_at: number;
  notes?:     string;
  // #1453 — per-credential hive grant. Absent on legacy rows; UI treats
  // missing as `"all"`/true (the spec-defined backfill default).
  hive_scope?:              HiveScope;
  include_invited_members?: boolean;
  // #3186 — per-key vault binding. Replaces the legacy "stuff
  // `vault=NAME` into the free-text `notes` field" UX. Empty / undefined
  // means the key is not yet bound to a vault. The 1Password provider is
  // the only one that surfaces a vault picker today, but the field lives
  // on the shared row so future credential-store providers can reuse it.
  vault_name?: string;
}

export const apiKeyApi = {
  list: (provider: string): Promise<ApiKeyEntry[]> =>
    adminFetch("GET", `/admin/apikey/${encodeURIComponent(provider)}`),

  create: (provider: string, label: string, key: string, notes?: string): Promise<void> =>
    adminFetch("POST", `/admin/apikey/${encodeURIComponent(provider)}`, {
      label, key, ...(notes ? { notes } : {}),
    }),

  update: (provider: string, label: string, key: string, notes?: string, newLabel?: string): Promise<void> =>
    adminFetch("PUT", `/admin/apikey/${encodeURIComponent(provider)}/${encodeURIComponent(label)}`, {
      label: newLabel ?? label, key, ...(notes !== undefined ? { notes } : {}),
    }),

  delete: (provider: string, label: string): Promise<void> =>
    adminFetch("DELETE", `/admin/apikey/${encodeURIComponent(provider)}/${encodeURIComponent(label)}`),

  /** #1453 — PATCH `/admin/integrations/apikey/<provider>/<key_label>/grant`.
   *  Sets `hive_scope` + `include_invited_members` on a single API-key row.
   *  Owner / tenant-admin only (server-enforced). */
  setGrant: (provider: string, keyLabel: string, grant: CredentialGrant): Promise<void> =>
    adminFetch(
      "PATCH",
      `/admin/integrations/apikey/${encodeURIComponent(provider)}/${encodeURIComponent(keyLabel)}/grant`,
      grant,
    ),
};

// ── 1Password vault items (#1976) ─────────────────────────────────────────────
// Read-only surface of the founder's 1Password vault items as credential rows
// on the existing 1Password provider card. Listing returns METADATA only;
// the value of an item's fields requires a separate one-time fetch.

export interface OpVaultItem {
  id:         string;
  title:      string;
  category:   string;
  updated_at: string;
}

export interface OpVaultTokenGroup {
  key_label:  string;
  vault_name: string;
  vault_id:   string;
  items:      OpVaultItem[];
  error:      string;
}

export interface OpVaultItemDetailField {
  label:   string;
  value:   string;
  purpose: string;
  type:    string;
}

export interface OpVaultItemDetail {
  id:         string;
  title:      string;
  category:   string;
  vault_id:   string;
  vault_name: string;
  updated_at: string;
  fields:     OpVaultItemDetailField[];
}

export const onepasswordVaultApi = {
  listItems: (): Promise<{ tokens: OpVaultTokenGroup[] }> =>
    adminFetch("GET", "/admin/integrations/onepassword/vault-items"),

  getItem: (itemId: string, vaultId: string, keyLabel?: string): Promise<OpVaultItemDetail> =>
    adminFetch("GET",
      `/admin/integrations/onepassword/vault-items/${encodeURIComponent(itemId)}` +
      `?vault_id=${encodeURIComponent(vaultId)}` +
      (keyLabel ? `&key_label=${encodeURIComponent(keyLabel)}` : "")),
};

// ── GitHub App install (#2603, parent #2573-C3) ───────────────────────────────
// Customer-org-level GitHub App ("2hives-ai-platform"). The install URL is
// opened in a popup/tab; on completion GitHub redirects back to the SPA
// callback (/admin/internal/github-app-callback) with installation_id +
// the state we minted (a single-use HMAC tying back to the caller's
// cognito_sub). The callback page POSTs to recordInstall() to persist the
// installation_id in the 2hives-github-installs DDB table (#2602).

export const GITHUB_APP_SLUG = "2hives-ai-platform";

export interface GitHubAppInstall {
  installation_id: number;
  account_login:   string;
  installed_at:    number;
}

/** Build the GitHub App "install on org" URL with our state token. */
export function githubAppInstallUrl(state: string): string {
  return `https://github.com/apps/${encodeURIComponent(GITHUB_APP_SLUG)}/installations/new` +
    `?state=${encodeURIComponent(state)}`;
}

export const githubAppApi = {
  /**
   * Mint a one-time state token bound to the caller's cognito_sub.
   * Backend signs state = HMAC(cognito_sub | nonce | exp). Returned to caller
   * so the browser can hand it to GitHub via the install URL's `state` param.
   */
  startInstall: (): Promise<{ state: string; install_url: string }> =>
    adminFetch("POST", "/admin/integrations/github-app/start"),

  /**
   * Callback handler — records installation_id in 2hives-github-installs.
   * Backend verifies `state` matches a freshly-minted token for the JWT
   * caller's cognito_sub before recording.
   */
  recordInstall: (installationId: number, state: string, setupAction?: string): Promise<GitHubAppInstall> =>
    adminFetch("POST", "/admin/integrations/github-app/callback", {
      installation_id: installationId,
      state,
      ...(setupAction ? { setup_action: setupAction } : {}),
    }),

  /** List installations recorded for the caller's account. */
  list: (): Promise<{ installs: GitHubAppInstall[] }> =>
    adminFetch("GET", "/admin/integrations/github-app/installs"),
};

// ── #2820 phase 1b/c/d — vault dropdown + key list + cross-card chips ────────
// Backed by `http_onepassword_keys.py` (FleetApiStack). Distinct from the
// phase-0 `onepasswordVaultApi` above which fronts the per-token vault-items
// reveal flow (#1976); the new endpoints below are tenant/service-account
// scoped and return EXISTENCE-ONLY data (no field values).

export interface OpVaultRef { id: string; name: string }

export interface OpKeyItem {
  id:         string;
  title:      string;
  category:   string;   // "LOGIN" | "API_CREDENTIAL"
  updated_at: string;
}

export interface OpVaultsResponse { vaults: OpVaultRef[]; cached_at: number; expires_at: number }
export interface OpKeysResponse   { vault_id: string; items: OpKeyItem[]; cached_at: number; expires_at: number }

export const onepasswordTenantApi = {
  listVaults: (): Promise<OpVaultsResponse> =>
    adminFetch("GET", "/admin/integrations/1password/vaults"),

  listKeys: (vaultId: string, opts?: { refresh?: boolean }): Promise<OpKeysResponse> =>
    adminFetch("GET",
      `/admin/integrations/1password/keys?vault_id=${encodeURIComponent(vaultId)}` +
      (opts?.refresh ? "&refresh=1" : "")),

  getDefaultVault: (): Promise<{ vault_id: string }> =>
    adminFetch("GET", "/admin/integrations/1password/default-vault"),

  setDefaultVault: (vaultId: string): Promise<{ ok: boolean; vault_id: string }> =>
    adminFetch("POST", "/admin/integrations/1password/default-vault", { vault_id: vaultId }),

  /** #3186 — per-key vault binding. Replaces the pre-#3186 UX of stuffing
   *  `vault=NAME` into the free-text `notes` field with a structured
   *  attribute on the 2hives-api-keys row. Lazy backfill on first read —
   *  rows that still carry the legacy `vault=NAME` notes get their
   *  `vault_name` populated server-side without a migration script. */
  listKeysVaultNames: (): Promise<{ keys: { key_label: string; vault_name: string }[] }> =>
    adminFetch("GET", "/admin/integrations/1password/keys-vault-names"),

  setKeyVault: (keyLabel: string, vaultName: string): Promise<{ ok: boolean; key_label: string; vault_name: string }> =>
    adminFetch(
      "PATCH",
      `/admin/integrations/1password/keys/${encodeURIComponent(keyLabel)}/vault`,
      { vault_name: vaultName },
    ),
};

// ── #2873 — Bitwarden vault dropdown + item list + cross-card chips ──────────
// Mirrors `onepasswordTenantApi` above. Backed by the Bitwarden tenant
// endpoints in FleetApiStack (card #2874). Bitwarden's data model calls
// these "collections" — we keep the "collection" naming on the URL + data
// layer (matches the Bitwarden API verbatim) but render them as "vault" in
// the UI so the founder sees a single consistent label across credential
// stores.

export interface BwCollectionRef { id: string; name: string }

export interface BwItem {
  id:         string;
  name:       string;
  type:       string;   // "login" | "secure_note" | "card" | "identity"
  updated_at: string;
}

export interface BwCollectionsResponse { collections: BwCollectionRef[]; cached_at: number; expires_at: number }
export interface BwItemsResponse       { collection_id: string; items: BwItem[]; cached_at: number; expires_at: number }

export const bitwardenTenantApi = {
  listCollections: (): Promise<BwCollectionsResponse> =>
    adminFetch("GET", "/admin/integrations/bitwarden/collections"),

  listItems: (collectionId: string, opts?: { refresh?: boolean }): Promise<BwItemsResponse> =>
    adminFetch("GET",
      `/admin/integrations/bitwarden/items?collection_id=${encodeURIComponent(collectionId)}` +
      (opts?.refresh ? "&refresh=1" : "")),

  getDefaultCollection: (): Promise<{ collection_id: string }> =>
    adminFetch("GET", "/admin/integrations/bitwarden/default-collection"),

  setDefaultCollection: (collectionId: string): Promise<{ ok: boolean; collection_id: string }> =>
    adminFetch("POST", "/admin/integrations/bitwarden/default-collection", { collection_id: collectionId }),
};
